import type { ReactNode } from 'react'
import { fmtMoney } from '../lib/money'
import { bucketPeriod, type DuePeriod } from '../lib/due-period'
import { Icon, type IconName } from './icon'

/** One big headline figure (e.g. total in EUR, or its UAH equivalent). */
export type SummaryStat = { label: string; value: string; tone?: string }

type Bucket = { within: number; uah: number; count: number }

export type SummaryBannerProps = {
  icon: IconName
  title: ReactNode
  /** Headline figures, shown first and prominent — in the order given. */
  totals: SummaryStat[]
  /** Already-pluralized phrase, e.g. "12 ліцензій до сплати". */
  countText: string
  fxIncomplete: boolean
  /** Section label for the due-date chips, e.g. "Терміни оплати". */
  periodsLabel: string
  overdueLabel?: string
  overdueUah: number
  overdueCount: number
  buckets: Bucket[]
  bucketLabels: Record<number, string>
  laterLabel: string
  laterUah: number
  /** Currently active period filter — the matching chip renders highlighted. */
  activePeriod?: DuePeriod | null
  /** Toggle a period filter by clicking its chip. When omitted, chips are static. */
  onPeriodToggle?: (period: DuePeriod) => void
}

function Stat({ label, value, tone }: SummaryStat) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--fg-4)',
        }}
      >
        {label}
      </span>
      <span className="num" style={{ fontSize: 22, fontWeight: 600, color: tone ?? 'var(--fg-1)' }}>
        {value}
      </span>
    </div>
  )
}

function PeriodChip({
  label,
  uah,
  count,
  urgent,
  active,
  onClick,
}: {
  label: string
  uah: number
  count: number
  urgent?: boolean
  active?: boolean
  /** When provided the chip becomes a filter toggle; otherwise it stays static. */
  onClick?: () => void
}) {
  const interactive = !!onClick
  // Active wins over urgent for the frame so the selected chip reads clearly even
  // on the red overdue background; the accent tint mirrors the table's selected row.
  const border = active
    ? 'var(--accent)'
    : urgent
      ? 'rgba(248,113,113,0.2)'
      : 'var(--border-default)'
  const background = active
    ? 'var(--accent-tint)'
    : urgent
      ? 'var(--danger-bg)'
      : 'var(--bg-3)'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      aria-pressed={interactive ? !!active : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        padding: '5px 10px',
        borderRadius: 6,
        textAlign: 'left',
        font: 'inherit',
        cursor: interactive ? 'pointer' : 'default',
        background,
        border: `1px solid ${border}`,
        boxShadow: active ? 'inset 2px 0 0 var(--accent)' : undefined,
      }}
    >
      <span style={{ fontSize: 10, color: urgent && !active ? 'var(--danger-fg)' : 'var(--fg-3)' }}>
        {label}
        {count > 0 ? ` · ${count}` : ''}
      </span>
      <span
        className="num"
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: urgent && !active ? 'var(--danger-fg)' : 'var(--fg-1)',
        }}
      >
        {fmtMoney(uah, 'UAH')}
      </span>
    </button>
  )
}

/**
 * A status banner for a money total split by due-date: headline figures first,
 * then an overdue chip and ≤7 / ≤14 / ≤30 / later chips. Shared by the license
 * debt bar ("our debt to Bitrix24") and the invoice receivable bar ("clients
 * owe us") — they differ only in labels and which figure leads.
 */
export function SummaryBanner({
  icon,
  title,
  totals,
  countText,
  fxIncomplete,
  periodsLabel,
  overdueLabel = 'Прострочено',
  overdueUah,
  overdueCount,
  buckets,
  bucketLabels,
  laterLabel,
  laterUah,
  activePeriod,
  onPeriodToggle,
}: SummaryBannerProps) {
  const toggle = (period: DuePeriod) =>
    onPeriodToggle ? () => onPeriodToggle(period) : undefined
  return (
    <div
      className="card"
      style={{
        padding: '14px 18px',
        marginBottom: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'center' }}>
          <Icon name={icon} size={14} color="var(--accent)" />
          <span style={{ color: 'var(--fg-2)' }}>{title}</span>
        </div>
        {totals.map((t) => (
          <Stat key={t.label} {...t} />
        ))}
        <div style={{ color: 'var(--fg-3)', fontSize: 12, alignSelf: 'center' }}>{countText}</div>
        {fxIncomplete && (
          <div style={{ color: 'var(--danger-fg)', fontSize: 12, alignSelf: 'center' }}>
            курс відсутній — UAH неповний
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          borderTop: '1px solid var(--border-subtle)',
          paddingTop: 10,
        }}
      >
        <span
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--fg-4)',
          }}
        >
          {periodsLabel}
        </span>
        {overdueUah > 0 && (
          <PeriodChip
            label={overdueLabel}
            uah={overdueUah}
            count={overdueCount}
            urgent
            active={activePeriod === 'overdue'}
            onClick={toggle('overdue')}
          />
        )}
        {buckets.map((b) => {
          const period = bucketPeriod(b.within)
          return (
            <PeriodChip
              key={b.within}
              label={bucketLabels[b.within] ?? `≤ ${b.within} дн`}
              uah={b.uah}
              count={b.count}
              urgent={b.within <= 7}
              active={activePeriod === period}
              // No rows behind an empty bucket — leave it static rather than
              // offering a filter that would land on an empty table.
              onClick={b.count > 0 ? toggle(period) : undefined}
            />
          )
        })}
        {laterUah > 0 && (
          <PeriodChip
            label={laterLabel}
            uah={laterUah}
            count={0}
            active={activePeriod === 'later'}
            onClick={toggle('later')}
          />
        )}
      </div>
    </div>
  )
}
